import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Stack,
  Alert,
  Switch,
  Typography,
  Box,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { importSourcesService } from '../../services/importSourcesService';
import { ImportSource, UpdateImportSourceRequest } from '../../types/api';
import { format } from 'date-fns';

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  url: z.string().url('Must be a valid URL'),
  importIntervalMinutes: z.number().min(5, 'Minimum 5 minutes').max(1440, 'Maximum 1440 minutes'),
  enabled: z.boolean(),
});

type FormData = z.infer<typeof schema>;

interface EditImportSourceDialogProps {
  open: boolean;
  source: ImportSource;
  onClose: () => void;
}

export const EditImportSourceDialog: React.FC<EditImportSourceDialogProps> = ({
  open,
  source,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: source.name,
      url: source.url,
      importIntervalMinutes: source.importIntervalMinutes,
      enabled: source.enabled,
    },
  });

  const enabled = watch('enabled');

  useEffect(() => {
    reset({
      name: source.name,
      url: source.url,
      importIntervalMinutes: source.importIntervalMinutes,
      enabled: source.enabled,
    });
  }, [source, reset]);

  const updateMutation = useMutation({
    mutationFn: (request: UpdateImportSourceRequest) => importSourcesService.update(source.id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['import-sources'] });
      handleClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update import source');
    },
  });

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  const onSubmit = (data: FormData) => {
    setError(null);
    updateMutation.mutate(data);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Import Source</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={3}>
            {error && <Alert severity="error">{error}</Alert>}

            {source.lastError && (
              <Alert severity="error">
                <Typography variant="body2" fontWeight="bold">
                  Last Error:
                </Typography>
                <Typography variant="body2">{source.lastError}</Typography>
              </Alert>
            )}

            {source.lastImported && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Last Import
                </Typography>
                <Typography variant="body1">
                  {format(new Date(source.lastImported), 'PPpp')}
                </Typography>
                {source.lastImportedCount !== null && source.lastImportedCount !== undefined && (
                  <Typography variant="body2" color="text.secondary">
                    Imported {source.lastImportedCount} entries
                  </Typography>
                )}
              </Box>
            )}

            <TextField
              label="Name"
              {...register('name')}
              error={!!errors.name}
              helperText={errors.name?.message}
              fullWidth
              required
            />

            <TextField
              label="URL"
              {...register('url')}
              error={!!errors.url}
              helperText={errors.url?.message}
              fullWidth
              required
            />

            <TextField
              label="Import Interval (Minutes)"
              type="number"
              {...register('importIntervalMinutes', { valueAsNumber: true })}
              error={!!errors.importIntervalMinutes}
              helperText={errors.importIntervalMinutes?.message || 'How often to check for updates (5-1440 minutes)'}
              fullWidth
              required
            />

            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setValue('enabled', e.target.checked)}
                />
              }
              label="Enabled"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={updateMutation.isPending}>
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
